import React, {
    memo, useCallback, useEffect, useLayoutEffect, useMemo, useState
} from "react";
import { useNavigate } from "react-router-dom";
import { withBase, HEADER } from "@/ui/Header";
import { PageShell } from "@/ui/PageShell";
import { goNativeHome } from "@/lib/nativeBridge";
import { registerPager, unregisterPager } from "@/lib/pagerBridge";
import { ViewportProbeModal } from '@/components/ViewportProbeModal';
import { readAndroidCssViewport } from "@/ui/androidViewport";
import { useFlexGapSupport } from "@/ui/useFlexGapSupport";
import { COLOR, FW, BORDER } from "@/ui/tokens";


/* ───────────────── Config ───────────────── */
const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");
import { toCoverUrl } from "@/lib/coverUrl";
import { installAndroidPickImageCallback, androidGetDeviceInfo, androidShowToast, SPC_MODEL, getDeviceModel, SPC_FONT_BOOST } from "@/lib/androidBridge";

// Figma 기준 고정 스펙
const BASE = {
    W: 1264, H: 1424,
    MARGIN_X: 108,
    // 헤더는 공통 컴포넌트 치수 사용
    SEARCH_W: 1048,
    SEARCH_H: 184,
    PROMPT_H: 40, PROMPT_FZ: 35,
    INPUT_W: 848, INPUT_H: 124,
    BTN_W: 175, BTN_H: 124,
    GAP: 24,
    CARD_W: 205, CARD_H: 295,
    TITLE_BOX_H: 130, TITLE_FZ: 32,
    GAP_COVER_TO_TITLE: 35,
    GRID_TOP_GAP: 10, GRID_ROW_GAP: 15,
    PAGER_H: 140,
} as const;

const ICONS = {
    first:  withBase('icons/prev2.svg'),
    prev:   withBase('icons/prev1.svg'),
    next:   withBase('icons/next1.svg'),
    last:   withBase('icons/next2.svg'),
    header: withBase('icons/search_on.svg'),
};

/* ──────────────── Utils / Hooks ─────────────── */
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

/** 검색 화면 전용 스케일: 가로/세로 둘 다 고려(헤더는 고정 150px) */
function useSearchScale() {
    const [r, setR] = React.useState(1);
    const [widthPx, setWidthPx] = React.useState(`${BASE.W}px`);
    const lastWRef = React.useRef(0);
    const lockRef = React.useRef(false); // IME(키보드) 중 잠금

    const compute = React.useCallback((w: number, vh: number) => {
          if (!w || !vh) return;

          // 1) 가로 기준 배율
          const rW = w / BASE.W;

          // 2) 세로 기준 배율 (2행 레이아웃이 한 화면에 들어가도록)

          const headerBase = HEADER.H; // 80
          const bodyAvail = Math.max(0, vh - headerBase);

          // r = 1일 때 body 높이 (검색 + 2행 그리드 + 페이저 + rowGap)
          const rowsBase = 2;
          const rowBlockBase =
                BASE.CARD_H + BASE.GAP_COVER_TO_TITLE + BASE.TITLE_BOX_H; // 295 + 35 + 130
          const gridHBase =
                BASE.GRID_TOP_GAP + rowsBase * rowBlockBase + BASE.GRID_ROW_GAP * (rowsBase - 1) - 50; // pagerLift=50

          const bodyBase =
                BASE.SEARCH_H + gridHBase + BASE.PAGER_H + BASE.GAP * 2; // search~grid, grid~pager gap 2개

          const rHRaw = bodyBase > 0 ? bodyAvail / bodyBase : 1;
          const rH = rHRaw * 0.97; // 3% 정도 여유

          // 3) 최종 배율 선택
          const minByVw =
                w >= 700 ? 0.56 :
                w >= 560 ? 0.50 :
                w >= 480 ? 0.42 : 0.30;

          let nr = Math.min(rW, rH, 1.2); // 가로/세로 제약 중 작은 쪽
          nr = Math.max(minByVw, nr);     // 너무 작아지지 않도록 하되

          // 컨테이너가 폭을 넘지 않도록 마지막 보정
              if (Math.round(BASE.W * nr) > w) {
          nr = rW;
          }

          setR(nr);
          const containerW = Math.min(Math.round(BASE.W * nr), w);
          setWidthPx(`${containerW}px`);
        }, []);

    React.useLayoutEffect(() => {
        const getSize = () => {
              const m = readAndroidCssViewport();
              const cssW = m?.cssW || 0;
              const cssH = m?.cssH || 0;
              const vw = window.innerWidth || document.documentElement.clientWidth || 0;
              const vh = window.innerHeight || document.documentElement.clientHeight || 0;
              const w = cssW && vw ? Math.min(cssW, vw) : (cssW || vw || BASE.W);
              const h = cssH && vh ? Math.min(cssH, vh) : (cssH || vh || BASE.H);
              return { w, h };
            };

        const apply = () => {
            if (lockRef.current) return;               // 키보드 중엔 무시
            const { w, h } = getSize();
            if (!w || !h) return;
            if (Math.abs(w - lastWRef.current) < 12) return;
            lastWRef.current = w;
            compute(w, h);
        };

        // 초기 계산
        const { w, h } = getSize();
        lastWRef.current = w;
        compute(w, h);

        const onResize = apply;
        const onOrient = () => {
            lockRef.current = false;
            // 약간의 지연 후 다시 계산(회전 애니메이션 안정화)
            setTimeout(() => {
                const { w, h } = getSize();
                lastWRef.current = w;
                compute(w, h);
            }, 60);
        };
        window.addEventListener('resize', onResize, { passive: true });
        window.addEventListener('orientationchange', onOrient);

        // 입력 포커스 시 잠금 → 키보드 표시/숨김 동안 r 고정
        const onFocusIn = (e: any) => {
            const tag = e.target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') lockRef.current = true;
        };
        const onFocusOut = () => setTimeout(() => (lockRef.current = false), 250);

        window.addEventListener('focusin', onFocusIn);
        window.addEventListener('focusout', onFocusOut);

        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onOrient);
            window.removeEventListener('focusin', onFocusIn);
            window.removeEventListener('focusout', onFocusOut);
        };
    }, [compute]);

    const s  = React.useCallback((n: number) => Math.round(n * r), [r]);
    const px = React.useCallback((n: number) => `${s(n)}px`, [s]);
    const isSpcDevice = React.useMemo(() => getDeviceModel() === SPC_MODEL, []);
    const sf = React.useCallback((n: number) => Math.round(n * r) + (isSpcDevice ? SPC_FONT_BOOST : 0), [r, isSpcDevice]);
    const sfpx = React.useCallback((n: number) => `${sf(n)}px`, [sf]);
    return { r, s, px, sf, sfpx, widthPx };
}


/** 비검색 텍스트 강조(안전 escape) */
function highlightHtml(text: string, q: string) {
    if (!text || !q) return text;
    try {
        const escQ = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const rx = new RegExp(`(${escQ})`, "gi");
        const esc = (s: string) => s.replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]!));
        return esc(text).replace(rx, "<b>$1</b>");
    } catch { return text; }
}

/** 윈도우 높이 기준 1~2행 자동 계산(헤더 150은 고정) */
function useAutoRowsWin(r: number, s: (n:number)=>number) {
    const [rows, setRows] = useState(2);
    useLayoutEffect(() => {
        const compute = () => {
            const css = getComputedStyle(document.documentElement)
                .getPropertyValue('--app-lvh').replace('px','').trim();
            const vh = Number(css) > 0
            ? Number(css)
                : (document.documentElement.clientHeight || window.innerHeight);

            const bodyH   = Math.max(0, vh - HEADER.H);
            const searchH = s(BASE.SEARCH_H);
            const pagerH  = s(BASE.PAGER_H);
            const gap2    = s(BASE.GAP) * 2;            // search~grid, grid~pager
            const gridTop = s(BASE.GRID_TOP_GAP);

            const oneRow  = s(BASE.CARD_H) + s(BASE.GAP_COVER_TO_TITLE) + s(BASE.TITLE_BOX_H);
            const inter   = s(BASE.GRID_ROW_GAP);

            const reservedBody = searchH + pagerH + gap2 + gridTop;
            const availGrid    = Math.max(0, bodyH - reservedBody);

            const rough = Math.floor((availGrid + inter) / (oneRow + inter));
            setRows(Math.max(1, Math.min(2, rough)) || 1);
        };

        compute();
        window.addEventListener("resize", compute);
        window.addEventListener("orientationchange", compute);
        return () => {
            window.removeEventListener("resize", compute);
            window.removeEventListener("orientationchange", compute);
        };
    }, [r, s]);
    return rows;
}

/* ──────────────── Recent Searches ─────────────── */
const RECENT_KEY = "bt.recentSearches";
const MAX_RECENT = 5;

function useRecentSearches(): [string[], (q: string) => void, (q: string) => void] {
    const [searches, setSearches] = useState<string[]>(() => {
        try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
    });

    const add = useCallback((q: string) => {
        const trimmed = q.trim();
        if (!trimmed) return;
        setSearches(prev => {
            const next = [trimmed, ...prev.filter(s => s !== trimmed)].slice(0, MAX_RECENT);
            try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
            return next;
        });
    }, []);

    const remove = useCallback((q: string) => {
        setSearches(prev => {
            const next = prev.filter(s => s !== q);
            try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
            return next;
        });
    }, []);

    return [searches, add, remove];
}


/* ──────────────── Popular Books ─────────────── */
function usePopularBooks(model: string) {
    const [books, setBooks] = useState<BookItem[]>([]);
    useEffect(() => {
        const limit = model === SPC_MODEL ? 3 : 4;
        const endpoint = API_BASE ? `${API_BASE}/books/popular?limit=${limit}` : `/books/popular?limit=${limit}`;
        fetch(endpoint, { headers: { Accept: "application/json" } })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.items) setBooks(d.items); })
            .catch(() => {});
    }, [model]);
    return books;
}

/* ──────────────── Types ─────────────── */
type BookItem = {
    title?: string;
    title_plain?: string;
    authors?: string;
    publisher?: string;
    isbn13?: string;
    isbn10?: string;
    cover_url?: string;
    id?: number;
};
type Meta = { total: number; total_pages: number; has_prev: boolean; has_next: boolean; provider: string };

/* ──────────────── Search Block ─────────────── */
const SearchBlock = memo(function SearchBlock({
                                                  input, setInput, onSubmit, s, px, sf, isSpc
                                              }: {
    input: string;
    setInput: (v: string) => void;
    onSubmit: (e: React.FormEvent) => void;
    s: (n: number) => number;
    px: (n: number) => string;
    sf: (n: number) => number;
    isSpc: boolean;
}) {
    const fz = Math.max(16, sf(28));
    const promptFz = Math.max(16, sf(BASE.PROMPT_FZ));
    const flexGapOK = useFlexGapSupport();
    const g = s(BASE.GAP);

    return (
        <div
            style={{
                width: "100%",                         // ✅ 부모 폭에 맞추고
                maxWidth: px(BASE.SEARCH_W),           // 최대 1048px까지만
                margin: "0 auto",                      // ✅ 가운데 정렬
                height: px(BASE.SEARCH_H),
                display: "grid",
                gridTemplateRows: `${px(BASE.PROMPT_H)} 1fr ${px(BASE.BTN_H)}`,
                justifyItems: "start",
                alignItems: "stretch",
                boxSizing: "border-box",
            }}
        >
            <p
                style={{
                    width: "100%",               // 전체 폭 사용
                    maxWidth: px(BASE.INPUT_W),
                    height: px(BASE.PROMPT_H),
                    lineHeight: px(BASE.PROMPT_H),
                    margin: 0,
                    fontSize: `${promptFz}px`,
                    fontWeight: FW.extraBold,
                    color: COLOR.text,
                }}
            >
                서재에 추가할 책을 검색합니다.
            </p>

            <div/>

            <form onSubmit={onSubmit} style={{alignSelf: "end"}}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        ...(flexGapOK ? { gap: px(BASE.GAP) } : {}),
                        minWidth: 0,
                    }}
                >
                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="책 제목을 입력하세요."
                        style={{
                            width: px(BASE.INPUT_W),
                            height: px(BASE.INPUT_H),
                            borderRadius: px(12),
                            padding: `0 ${px(16)}`,
                            border: BORDER.boldDark,
                            outline: "none",
                            color: COLOR.black,
                            fontSize: `${fz}px`,
                            fontWeight: FW.extraBold,
                            backgroundColor: COLOR.white,
                            boxSizing: "border-box",
                            flex: "0 0 auto",

                        }}
                    />
                    <button
                        type="submit"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: px(BASE.BTN_W),
                            height: px(BASE.BTN_H),
                            borderRadius: px(12),
                            fontSize: `${fz}px`,
                            fontWeight: FW.extraBold,
                            color: COLOR.white,
                            background: COLOR.black,
                            border: "none",
                            cursor: "pointer",
                            WebkitAppearance: "none",
                            appearance: "none",
                            WebkitTapHighlightColor: "transparent",
                            outline: "none",
                            flexShrink: 0,
                            transform: "translateZ(0)",
                            ...(flexGapOK ? {} : { marginLeft: `${g}px` }),
                        }}
                    >
                        검색
                    </button>
                </div>
            </form>
        </div>
    );
});

/* ──────────────── Book Card ─────────────── */
const BookCard = memo(function BookCard({
                                            book, q, cardW, coverH, titlePx, titleBoxH, gapCoverToTitle, onClick
                                        }: {
    book: BookItem; q: string; cardW: number; coverH: number; titlePx: number; titleBoxH: number;
    gapCoverToTitle: number; onClick?: (b: BookItem) => void;
}) {
    const titleHtml = useMemo(
        () => highlightHtml(book.title_plain || book.title || "", q),
        [book.title, book.title_plain, q]
    );

    return (
        <button
            style={{
                width: cardW,
                textAlign: "left",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer"
            }}
            onClick={() => onClick?.(book)}
            aria-label={(book.title_plain || book.title || "책").replace(/<[^>]+>/g, "")}
        >
            <div
                style={{
                    width: cardW, height: coverH, overflow: "hidden",
                    borderRadius: 0, border: "none",
                    background: COLOR.bgLighter, boxShadow: "none",
                }}
            >
                {book.cover_url ? (
                    <img
                        src={toCoverUrl(book.cover_url)}
                        alt={book.title_plain || book.title || "cover"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                            // 리퍼러/도메인 이슈로 403 등 실패 시, 한 번만 빈 이미지로 폴백
                            // (무한 루프 방지)
                            const img = e.currentTarget as HTMLImageElement;
                            if (img.dataset._failed !== "1") {
                                img.dataset._failed = "1";
                                img.src = ""; // 또는 프로젝트 기본 placeholder 경로로 교체 가능
                            }
                        }}
                    />
                ) : (
                    <div style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#9ca3af",
                        fontSize: 12
                    }}>
                        No Image
                    </div>
                )}
            </div>

            <div style={{
                marginTop: gapCoverToTitle,
                height: titleBoxH,
                width: "100%",
                overflow: "hidden",
                paddingBottom: 2
            }}>
                <div
                    style={{
                        fontWeight: FW.extraBold, fontSize: titlePx, lineHeight: 1.22, display: "-webkit-box",
                        WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"
                    }}
                    dangerouslySetInnerHTML={{__html: titleHtml}}
                />
            </div>
        </button>
    );
});

/* ──────────────── Grid (cards) ─────────────── */
const CardGrid = memo(function CardGrid({
                                            items, loading, error, query, cols, rows, dims, onCardClick, px, sfpx,
                                            recentSearches, onSelectRecent, onRemoveRecent,
                                            popularBooks, deviceModel,
                                        }: {
    items: BookItem[]; loading: boolean; error: string | null; query: string;
    cols: number; rows: number;
    dims: { cardW: number; coverH: number; titlePx: number; titleBoxH: number; gapCoverToTitle: number; colGap: number; gridH: number; };
    onCardClick: (b: BookItem)=>void;
    px: (n: number) => string;
    sfpx: (n: number) => string;
    recentSearches: string[];
    onSelectRecent: (q: string) => void;
    onRemoveRecent: (q: string) => void;
    deviceModel: string;
    popularBooks: BookItem[];
}) {
    const skeletons = useMemo(() => Array.from({ length: cols * rows }), [cols, rows]);

    return (
        <div
         style={{
             width: "100%",
             maxWidth: px(BASE.SEARCH_W),
             margin: "0 auto",
             paddingTop: px(BASE.GRID_TOP_GAP),
             height: `${dims.gridH}px`,
             overflow: "hidden",
             boxSizing: "border-box",
            }}
    >
        {!query ? (
            <div style={{ display: "flex", flexDirection: "column", gap: px(32), height: "100%", overflow: "hidden" }}>
                {/* 최근 검색어 */}
                {recentSearches.length > 0 && (
                    <div>
                        <p style={{ margin: `0 0 ${px(12)} 0`, fontSize: sfpx(26), fontWeight: 700, color: "#555" }}>
                            최근 검색어
                        </p>
                        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: px(4) }}>
                            {recentSearches.map((q) => (
                                <li key={q} style={{ display: "flex", alignItems: "center", gap: px(12) }}>
                                    <button
                                        onClick={() => onSelectRecent(q)}
                                        style={{
                                            flex: 1,
                                            textAlign: "left",
                                            background: "none",
                                            border: "none",
                                            padding: `${px(6)} 0`,
                                            fontSize: sfpx(28),
                                            fontWeight: FW.bold,
                                            color: COLOR.text,
                                            cursor: "pointer",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {q}
                                    </button>
                                    <button
                                        onClick={() => onRemoveRecent(q)}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            fontSize: sfpx(22),
                                            color: "#9ca3af",
                                            cursor: "pointer",
                                            flexShrink: 0,
                                            padding: `0 ${px(4)}`,
                                            lineHeight: 1,
                                        }}
                                        aria-label="삭제"
                                    >
                                        ✕
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* 인기도서 */}
                {popularBooks.length > 0 && (
                    <div>
                        <p style={{ margin: `0 0 ${px(16)} 0`, fontSize: sfpx(26), fontWeight: 700, color: "#555" }}>
                            인기도서
                        </p>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: deviceModel === SPC_MODEL
                                ? `repeat(3, ${dims.cardW}px)`
                                : `repeat(${popularBooks.length}, ${dims.cardW}px)`,
                            columnGap: `${dims.colGap}px`,
                            rowGap: `${dims.colGap}px`,
                        }}>
                            {popularBooks.map((b, idx) => (
                                <BookCard
                                    key={`pop-${b.isbn13 || b.isbn10 || idx}`}
                                    book={b}
                                    q=""
                                    cardW={dims.cardW}
                                    coverH={dims.coverH}
                                    titlePx={dims.titlePx}
                                    titleBoxH={dims.titleBoxH}
                                    gapCoverToTitle={dims.gapCoverToTitle}
                                    onClick={onCardClick}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
            ) : (
                <>
                    {error && <div style={{ marginTop: 8, color: "#dc2626", fontSize: 14 }}>{error}</div>}

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${cols}, minmax(0, ${dims.cardW}px))`,
                            justifyContent: "start",
                            columnGap: `${dims.colGap}px`,
                            rowGap: px(BASE.GRID_ROW_GAP),
                            alignContent: "start",
                        }}
                    >
                        {loading && items.length === 0
                            ? skeletons.map((_, i) => (
                                    <div key={i} style={{ width: dims.cardW }}>
                                        <div style={{ height: dims.coverH,  background: "#e5e7eb" }} />
                                        <div style={{ height: dims.titleBoxH, width: "100%", marginTop: dims.gapCoverToTitle, background: "#e5e7eb" }} />
                                    </div>
                            ))
                            : items.map((b, idx) => (
                                <BookCard
                                    key={`${b.isbn13 || b.isbn10 || b.title || "no-key"}-${idx}`}
                                    book={b} q={query}
                                    cardW={dims.cardW}
                                    coverH={dims.coverH}
                                    titlePx={dims.titlePx}
                                    titleBoxH={dims.titleBoxH}
                                    gapCoverToTitle={dims.gapCoverToTitle}
                                    onClick={onCardClick}
                                />
                            ))}
                    </div>

                    {!loading && items.length === 0 && (
                        <div style={{ paddingTop: 16, color: "#555", fontSize: 14 }}>검색 결과가 없습니다.</div>
                    )}
                </>
            )}
        </div>
    );
});

/* ──────────────── Pager ─────────────── */
const Pager = memo(function Pager({
                                      hasResults, page, totalPages, hasNext, onFirst, onPrev, onNext, onLast, px, sfpx
                                  }: {
    hasResults: boolean; page: number; totalPages: number; hasNext: boolean;
    onFirst: ()=>void; onPrev: ()=>void; onNext: ()=>void; onLast: ()=>void;
    px: (n:number)=>string; sfpx: (n:number)=>string;
}) {
    if (!hasResults) return <div style={{ height: px(BASE.PAGER_H) }} />;
    return (
        <div style={{ width: "100%", height: px(BASE.PAGER_H) }}>
            <div style={{ display: "flex", alignItems:"center", justifyContent:"center", height: "100%" }}>
                <button disabled={!(page > 1)} onClick={onFirst} aria-label="처음"
                        style={{ lineHeight:0, background:"transparent", marginRight:px(48), border:"none", opacity:page>1?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.first} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
                <button disabled={!(page > 1)} onClick={onPrev} aria-label="이전"
                        style={{ lineHeight:0, background:"transparent", marginRight:px(46), border:"none", opacity:page>1?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.prev} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
                <span style={{ fontWeight:700, fontFeatureSettings:"'tnum' 1", fontSize:sfpx(34), minWidth:px(120), textAlign:"center", marginRight:px(46) }}>
          {Math.max(1, page)} / {Math.max(1, totalPages)}
        </span>
                <button disabled={!hasNext} onClick={onNext} aria-label="다음"
                        style={{ lineHeight:0, background:"transparent", marginRight:px(48), border:"none", opacity:hasNext?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.next} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
                <button disabled={!hasNext} onClick={onLast} aria-label="마지막"
                        style={{ lineHeight:0, background:"transparent", border:"none", opacity:hasNext?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.last} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
            </div>
        </div>
    );
});

/* ──────────────── Page ─────────────── */
export default function BookSearchPage() {
    const { r, s, px, sf, sfpx, widthPx } = useSearchScale();
    const deviceModel = getDeviceModel();
    const isSpc = deviceModel === SPC_MODEL;
    const rows = 2;
    const colsFixed = isSpc ? 3 : 4;
    const navigate = useNavigate();

    const [probeOpen, setProbeOpen] = React.useState(false);
    const [recentSearches, addRecentSearch, removeRecentSearch] = useRecentSearches();
    const popularBooks = usePopularBooks(deviceModel);

            const dims = useMemo(() => {
            const innerW = s(BASE.SEARCH_W);
            const cols = colsFixed;
            const colGap = s(BASE.GAP);

            // SPC: fill width with bigger cards; others: keep original card size, spread gap
            const cardW = isSpc
                ? Math.floor((innerW - colGap * (cols - 1)) / cols) - 5
                : s(BASE.CARD_W);
            const finalColGap = isSpc
                ? colGap
                : Math.max(colGap, Math.round((innerW - cardW * cols) / (cols - 1)));

            // scale cover/title proportionally to cardW (only grows for SPC)
            const ratio  = cardW / s(BASE.CARD_W);
            const coverH          = Math.round(s(BASE.CARD_H)            * ratio);
            const titlePx         = Math.round(s(BASE.TITLE_FZ)          * ratio);
            const titleBoxH       = Math.round(s(BASE.TITLE_BOX_H)       * ratio);
            const gapCoverToTitle = Math.round(s(BASE.GAP_COVER_TO_TITLE)* ratio);

        // 그리드 세로 높이(픽셀) 고정 → 페이징이 한 화면에 항상 보이도록
         const topGap = s(BASE.GRID_TOP_GAP);
         const rowGap = s(BASE.GRID_ROW_GAP);
         const rowBlock = coverH + gapCoverToTitle + titleBoxH;
         const pagerLift = s(50);
         const gridH = topGap + rows * rowBlock + rowGap * (rows - 1) - pagerLift;

            return { innerW, cols, cardW, coverH, titlePx, titleBoxH, gapCoverToTitle, colGap: finalColGap, gridH };
        }, [s, isSpc, colsFixed]);

     const cols = dims.cols;

    const rawPageSize = useMemo(() => cols * rows, [cols, rows]);
    const pageSize = Math.max(1, rawPageSize);

    const [input, setInput] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem("bt.bookSearch") || "null")?.inp || ""; } catch { return ""; }
    });
    const [query, setQuery] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem("bt.bookSearch") || "null")?.q || ""; } catch { return ""; }
    });
    const [page, setPage] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem("bt.bookSearch") || "null")?.p || 1; } catch { return 1; }
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [items, setItems] = useState<BookItem[]>([]);
    const [meta, setMeta] = useState<Meta>({ total: 0, total_pages: 1, has_prev: false, has_next: false, provider: "db" });

    // 복원 후 sessionStorage 정리 (한 번만)
    useEffect(() => { sessionStorage.removeItem("bt.bookSearch"); }, []);

    const onSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        const q = input.trim();
        if (q) addRecentSearch(q);
        setPage(1); setQuery(q);
    }, [input, addRecentSearch]);

    const onSelectRecent = useCallback((q: string) => {
        setInput(q); addRecentSearch(q); setPage(1); setQuery(q);
    }, [addRecentSearch]);

    const openDetail = useCallback((b: BookItem) => {
        const id = b.isbn13 || b.isbn10 || (b.id ? `db-${b.id}` : `work-${encodeURIComponent((b.title_plain || b.title || "").slice(0, 80))}`);
        sessionStorage.setItem("bt.bookSearch", JSON.stringify({ q: query, inp: input, p: page }));
        navigate(`/books/${id}`, { state: { book: b, from: { q: query, page } } });
    }, [navigate, query, input, page]);

    // 물리키 페이저 브릿지 등록
    useEffect(() => {
        registerPager({
            next: () => setPage(p => Math.min(meta.total_pages, p + 1)),
            prev: () => setPage(p => Math.max(1, p - 1)),
            first: () => setPage(1),
            last: () => setPage(meta.total_pages || 1),
        });
        return () => unregisterPager();
    }, [meta.total_pages]);

    useEffect(() => {
        const controller = new AbortController();
        const run = async () => {
            if (!query) { setItems([]); setMeta({ total:0, total_pages:1, has_prev:false, has_next:false, provider:"db" }); return; }
            setLoading(true); setError(null);
            try {
                const endpoint = API_BASE ? `${API_BASE}/books/search` : `/books/search`;
                const params = new URLSearchParams({ q: query, page: String(page), size: String(pageSize), ext: "1" });
                const r = await fetch(`${endpoint}?${params.toString()}`, { headers: { Accept: "application/json" }, signal: controller.signal });
                const ctype = r.headers.get('content-type') || '';
                if (!r.ok) {
                    // 사용자 친화적 메시지
                    if (r.status === 400) throw new Error("검색어를 확인해주세요.");
                    if (r.status === 404) throw new Error("검색 결과가 없습니다.");
                    throw new Error("검색 중 오류가 발생했습니다.");
                }
                let data: any;
                try { data = await r.json(); }
                catch {
                    if (!ctype.toLowerCase().includes('json')) {
                        const t = await r.text();
                        throw new Error(`JSON 파싱 실패: ${t.slice(0,200)}`);
                    } else { throw new Error('JSON 파싱 실패'); }
                }
                const total = Number(data.total || 0);
                const total_pages = Math.max(1, Math.ceil(total / pageSize));
                const has_prev = page > 1;
                const has_next = page < total_pages && (data.items?.length ?? 0) > 0;
                const normalizeItems = (arr:any[]=[]) => arr.map((it:any) => ({
                    ...it,
                    cover_url: it.cover_url || it.coverUrl || it.cover || it.image_url || it.image || ""
                }));
                setItems(normalizeItems(data.items || []));
                setMeta({
                    total,
                    total_pages: data.total_pages || total_pages,
                    has_prev: data.has_prev ?? has_prev,
                    has_next: data.has_next ?? has_next,
                    provider: data.query?.provider || (data as any).provider || "db",
                });
            } catch (e:any) {
                if (e?.name === 'AbortError') return;
                // 이미 친화적 메시지면 그대로, 아니면 일반 메시지
                const msg = e?.message || '';
                const friendly = msg.includes("검색") ? msg : "검색 중 오류가 발생했습니다.";
                setError(friendly);
            } finally {
                // 취소된 경우 로딩 토글하지 않음
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        run();
        return () => controller.abort();
    }, [query, page, pageSize]);

    const hasResults = items.length > 0;

    const handleHomeFromHeader = React.useCallback(() => {
           const ok = goNativeHome();
           if (!ok) {
                       }
    }, []);


    return (
        <PageShell
            title="책 검색"
            icon={ICONS.header}
            onTitleClick={handleHomeFromHeader}
            onIconClick={handleHomeFromHeader}
            width={widthPx}
        >
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    paddingTop: isSpc ? `${s(16)}px` : 0,
                    display: "grid",
                    gridTemplateAreas: `"search" "grid" "pager"`,
                    gridTemplateRows: `${px(BASE.SEARCH_H)} 1fr ${px(BASE.PAGER_H)}`,
                    gridTemplateColumns: "1fr",
                    gridRowGap: px(BASE.GAP),
                    boxSizing: "border-box",
                }}
            >
                {/* 검색 */}
                <div style={{ gridArea: "search" }}>
                    <SearchBlock input={input} setInput={setInput} onSubmit={onSubmit} s={s} px={px} sf={sf} isSpc={isSpc} />
                </div>

                {/* 그리드 */}
                <div style={{ gridArea: "grid" }}>
                    <CardGrid
                        items={items} loading={loading} error={error} query={query}
                        cols={dims.cols} rows={rows}
                        dims={{
                            cardW: dims.cardW, coverH: dims.coverH, titlePx: dims.titlePx,
                            titleBoxH: dims.titleBoxH, gapCoverToTitle: dims.gapCoverToTitle,
                            colGap: dims.colGap, gridH: dims.gridH,
                        }}
                        onCardClick={openDetail}
                        px={px}
                        sfpx={sfpx}
                        recentSearches={recentSearches}
                        onSelectRecent={onSelectRecent}
                        onRemoveRecent={removeRecentSearch}
                        popularBooks={popularBooks}
                        deviceModel={deviceModel}
                    />
                </div>

                {/* 페이징 */}
                <div style={{ gridArea: "pager" }}>
                    <div style={{ marginLeft: px(BASE.MARGIN_X), marginRight: px(BASE.MARGIN_X), marginTop: px(22), }}>
                        <Pager
                            hasResults={hasResults}
                            page={page}
                            totalPages={meta.total_pages}
                            hasNext={meta.has_next}
                            onFirst={() => setPage(1)}
                            onPrev={() => setPage(p => Math.max(1, p - 1))}
                            onNext={() => setPage(p => Math.min(meta.total_pages, p + 1))}
                            onLast={() => setPage(meta.total_pages || 1)}
                            px={px}
                            sfpx={sfpx}
                        />
                    </div>
                </div>
            </div>

            {/* 디버그 버튼 */}
            {/*<button*/}
            {/*    onClick={()=>setProbeOpen(true)}*/}
            {/*    style={{*/}
            {/*        position:'fixed', right:16, bottom:16, zIndex:2100,*/}
            {/*        padding:'10px 12px', borderRadius:10, border:'1px solid #000',*/}
            {/*        background:'#000', color:'#fff', fontWeight:800, cursor:'pointer'*/}
            {/*    }}*/}
            {/*>*/}
            {/*    VIEWPORT*/}
            {/*</button>*/}

            {/* 모달 */}
            <ViewportProbeModal open={probeOpen} onClose={()=>setProbeOpen(false)} />

        </PageShell>
    );
}
